// ============================================================
// AgentConsole.js — Redesigned
// Three panels: Softphone | Live Transcript | AI Assistant
// Dark teal theme, no Infra Dashboard
// ============================================================
import { useState, useRef, useEffect } from 'react';
import WebRTCAgent from './WebRTCAgent';
import { useCall } from './CallContext';

const T = {
  bg:       '#0d1f1f', bgPanel: '#112828', bgCard: '#163333', bgInput: '#0a1a1a',
  border:   '#1e3f3f', borderHi: '#2a5a5a', teal: '#00bfa5', tealDim: '#007a6a',
  tealGlow: '#00e5c8', text: '#e0f2f0', textSub: '#7ab8b0', textDim: '#3d7a72',
  green: '#00c853', red: '#ff5252', amber: '#ffab40', blue: '#40c4ff',
};

export default function AgentConsole({ token }) {
  const { liveTranscript, callStatus, activeCall } = useCall();

  // AI Assistant chat
  const [chatMsgs,  setChatMsgs]  = useState([
    { role: 'ai', text: 'Ready. Ask me anything about Linux administration, server troubleshooting, or DevOps.' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy,  setChatBusy]  = useState(false);
  const chatEndRef  = useRef(null);
  const txEndRef    = useRef(null);
  const sessionId   = useRef('agent_' + Date.now());

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMsgs]);
  useEffect(() => { txEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [liveTranscript]);

  const sendChat = async (text) => {
    if (!text.trim() || chatBusy) return;
    setChatBusy(true);
    setChatMsgs(p => [...p, { role: 'user', text }]);

    // Build conversation history for context
    const history = chatMsgs
      .filter(m => m.role !== 'system')
      .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.text}`)
      .join('\n');

    try {
      // Use streaming chat endpoint - pure LLM answer, no command execution
      const r = await fetch('/api/ask-assistant', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text, history, session_id: sessionId.current }),
      });

      if (!r.ok) throw new Error('Server error: ' + r.status);

      // Handle streaming response
      if (r.headers.get('content-type')?.includes('text/event-stream') ||
          r.headers.get('content-type')?.includes('text/plain')) {
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = '';
        setChatMsgs(p => [...p, { role: 'ai', text: '' }]);
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          setChatMsgs(p => [...p.slice(0, -1), { role: 'ai', text: buf }]);
        }
      } else {
        const d = await r.json();
        setChatMsgs(p => [...p, { role: 'ai', text: d.answer || d.text || 'No response' }]);
      }
    } catch (e) {
      setChatMsgs(p => [...p, { role: 'ai', text: 'Connection error: ' + e.message }]);
    } finally { setChatBusy(false); }
  };

  // Status pill
  const statusPill = {
    idle:    { bg: '#0a2a20', color: T.textDim, label: 'No active call', dot: T.textDim },
    ringing: { bg: '#2a1e0a', color: T.amber,   label: 'Ringing...',     dot: T.amber  },
    active:  { bg: '#0a2a18', color: T.green,   label: 'Call active',    dot: T.green  },
  }[callStatus] || { bg: '#0a2a20', color: T.textDim, label: 'No active call', dot: T.textDim };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr 320px',
                  height: '100%', overflow: 'hidden', background: T.bg }}>

      {/* ── LEFT: Softphone ───────────────────────────────────── */}
      <div style={{ background: T.bgPanel, borderRight: `1px solid ${T.border}`,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Panel header */}
        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Voice Agent</div>
            <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>WebRTC · agent-webrtc</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                        borderRadius: 20, background: statusPill.bg }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusPill.dot,
                          boxShadow: callStatus === 'active' ? `0 0 6px ${T.green}` : 'none' }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: statusPill.color }}>{statusPill.label}</span>
          </div>
        </div>

        {/* Dial info bar */}
        <div style={{ padding: '8px 16px', borderBottom: `1px solid ${T.border}`,
                      background: T.bgCard, fontSize: 12, color: T.textDim }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>DID 1000 → Human Agent</span>
            <span style={{ color: T.teal }}>DID 1002 → AI Bot</span>
          </div>
        </div>

        {/* WebRTC agent */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <WebRTCAgent colors={T} />
        </div>
      </div>

      {/* ── CENTRE: Live Transcript ───────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', background: T.bg }}>

        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      background: T.bgPanel }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Live Conversation</div>
            <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Real-time speech-to-text transcript</div>
          </div>
          {callStatus === 'active' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px',
                          borderRadius: 20, background: '#0a2a18', border: `1px solid ${T.green}40` }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.green,
                            animation: 'pulse 1.5s infinite' }} />
              <span style={{ fontSize: 10, fontWeight: 600, color: T.green }}>Recording</span>
            </div>
          )}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px',
                      display: 'flex', flexDirection: 'column', gap: 12 }}>
          {liveTranscript.length === 0 && (
            <div style={{ textAlign: 'center', marginTop: 80, color: T.textDim }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎙</div>
              <div style={{ fontSize: 16, color: T.textSub }}>Transcript appears here during an active call</div>
              <div style={{ fontSize: 13, marginTop: 8 }}>Call 1000 to connect with the human agent</div>
            </div>
          )}

          {liveTranscript.map((seg, i) =>
            seg.isMarker ? (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, color: T.textDim,
                                    padding: '8px 0', borderTop: `1px dashed ${T.border}` }}>
                {seg.text}
              </div>
            ) : (
              <div key={i} style={{ display: 'flex', flexDirection: 'column',
                                    alignItems: seg.speaker === 'agent' ? 'flex-end' : 'flex-start' }}>
                <div style={{ fontSize: 11, color: T.textDim, marginBottom: 4, textTransform: 'uppercase',
                              letterSpacing: 0.8, fontWeight: 700 }}>
                  {seg.speaker === 'agent' ? '👤 Agent' : '📞 Caller'} · {new Date(seg.ts).toLocaleTimeString()}
                </div>
                <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: 12,
                              background: seg.speaker === 'agent' ? T.tealDim + '40' : T.bgCard,
                              border: `1px solid ${seg.speaker === 'agent' ? T.teal + '40' : T.border}`,
                              fontSize: 15, lineHeight: 1.6, color: T.text }}>
                  {seg.text}
                </div>
              </div>
            )
          )}
          <div ref={txEndRef} />
        </div>
      </div>

      {/* ── RIGHT: AI Assistant ───────────────────────────────── */}
      <div style={{ background: T.bgPanel, borderLeft: `1px solid ${T.border}`,
                    display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{ padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>AI Assistant</div>
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Linux knowledge · Local LLM</div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex',
                      flexDirection: 'column', gap: 10 }}>
          {chatMsgs.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column',
                                  alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '90%', padding: '9px 12px', borderRadius: 10,
                            background: m.role === 'user' ? T.tealDim + '60' : T.bgCard,
                            border: `1px solid ${m.role === 'user' ? T.teal + '40' : T.border}`,
                            fontSize: 13, lineHeight: 1.6, color: T.text,
                            whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {m.text}
              </div>
            </div>
          ))}
          {chatBusy && (
            <div style={{ fontSize: 11, color: T.textSub, display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 5, height: 5, borderRadius: '50%', background: T.teal }} />
              Thinking...
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div style={{ padding: 12, borderTop: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { sendChat(chatInput); setChatInput(''); } }}
              placeholder="Ask about Linux..."
              style={{ flex: 1, padding: '8px 10px', background: T.bgInput,
                       border: `1px solid ${T.border}`, borderRadius: 8,
                       color: T.text, fontSize: 13, outline: 'none' }} />
            <button onClick={() => { sendChat(chatInput); setChatInput(''); }}
              disabled={chatBusy || !chatInput.trim()}
              style={{ background: T.teal, border: 'none', borderRadius: 8, padding: '8px 14px',
                       fontSize: 13, fontWeight: 700, color: '#000',
                       opacity: chatBusy || !chatInput.trim() ? 0.5 : 1 }}>
              Send
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
      `}</style>
    </div>
  );
}
